// SPDX-License-Identifier: AGPL-3.0-only
// Copyright (C) 2025 realvirtual GmbH <https://realvirtual.io>

/**
 * CollisionAlertPlugin — collision cards in the right-side messages panel
 * (plan-394; card form replaced the original modal on user decision
 * 2026-08-07: "handle it like the error messages — no own overlay window").
 *
 * One TileCard per LATCHED pair, fed by the collision manager via
 * `collision-alert-store`. Cards persist after the geometry separates (user
 * decision: no auto-clear) until one of:
 *   - the card's **OK** action — acknowledges exactly this pair (it returns
 *     on the next tick while the geometry still intersects),
 *   - the card's **Ignore** action — suppresses that collision TYPE
 *     (role pair, e.g. Tool↔Workpiece) for the rest of the current run,
 *   - a rising `ResetCollisions` edge from the PLC,
 *   - a model change.
 *
 * Clicking a card frames the camera on the collided objects and flashes them
 * in the warning color (`pulseSeverityOutline`); the PERSISTENT outline on
 * the pair is the OutlinePass status silhouette, owned by the manager.
 */

import type { MouseEvent } from 'react';
import { Box, Button } from '@mui/material';
import type { RVViewerPlugin } from '../core/rv-plugin';
import type { UISlotEntry, UISlotProps } from '../core/rv-ui-plugin';
import { useCollisionAlert, type CollisionPairView } from '../core/hmi/collision-alert-store';
import { TileCard } from '../core/hmi/TileCard';
import { pulseSeverityOutline } from '../core/hmi/severity-pulse';

/** Format a performance.now() timestamp to a wall-clock HH:MM:SS string. */
function formatSince(since: number): string {
  const epochMs = since + (Date.now() - performance.now());
  const d = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** A `mu:<id>` key has no scene path to focus — filter those out. */
function focusablePaths(view: CollisionPairView): string[] {
  return [view.aKey, view.bKey].filter((k) => !k.startsWith('mu:'));
}

function CardActions({ viewer, view }: { viewer: UISlotProps['viewer']; view: CollisionPairView }) {
  // TileCard contract: footer buttons must not trigger the card-body click.
  const acknowledge = (e: MouseEvent) => {
    e.stopPropagation();
    viewer.collisionManager.acknowledge(view.aKey, view.bKey);
  };
  const ignoreType = (e: MouseEvent) => {
    e.stopPropagation();
    viewer.collisionManager.ignoreType(view.aRole, view.bRole);
  };
  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="warning"
        data-testid="collision-ok"
        onClick={acknowledge}
        sx={{ textTransform: 'none', fontSize: 12, py: 0.25, minWidth: 48 }}
      >
        OK
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        data-testid="collision-ignore-type"
        onClick={ignoreType}
        sx={{ textTransform: 'none', fontSize: 12, py: 0.25, ml: 1 }}
      >
        Ignore {view.aRole} ↔ {view.bRole}
      </Button>
    </>
  );
}

// ─── Panel ──────────────────────────────────────────────────────────────

export function CollisionAlertPanel({ viewer }: UISlotProps) {
  const snap = useCollisionAlert();
  if (snap.pairs.length === 0) return null;

  return (
    <Box
      data-ui-panel
      data-testid="collision-alert-panel"
      sx={{ pointerEvents: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      {snap.pairs.map((v) => {
        const paths = focusablePaths(v);
        return (
          <Box key={`${v.aKey} ${v.bKey}`} data-testid="collision-card">
            <TileCard
              title={`Collision: ${v.aPath} ↔ ${v.bPath}`}
              subtitle={`${v.aRole} ↔ ${v.bRole}`}
              severity="warning"
              icon="warning"
              timestamp={formatSince(v.since)}
              componentPath={paths[0]}
              onAction={paths.length > 0
                ? () => pulseSeverityOutline(viewer, paths, 'warning')
                : undefined}
              actions={<CardActions viewer={viewer} view={v} />}
            />
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Plugin ─────────────────────────────────────────────────────────────

export class CollisionAlertPlugin implements RVViewerPlugin {
  readonly id = 'collision-alert';
  readonly slots: UISlotEntry[] = [
    // Right below the WebError cards (order 5) in the messages slot.
    { slot: 'messages', component: CollisionAlertPanel, order: 6 },
  ];
}
